import React from 'react';
import { useIntl } from 'react-intl';
import {
  Checkbox,
  Form,
  Input,
  Modal,
} from 'semantic-ui-react';
import {
  firstError, firstResponseError, pruneServerErrors, checkRequired, checkMaxLength, validationClasses,
} from '@/utils/errors';
import { states } from '@/constants/lifecycle';
import fields, { globalFieldLengths } from '@/constants/fields';
import Actions from '@/components/buttons/Actions';
import FormValidationError from '@/components/errors/FormValidationError';
import DialogWrapper from '@/components/dialogs/DialogWrapper';
import MarkdownEditor from '@/components/text-editors/MarkdownEditor';
import selectors from '../hooks/selectors';
import events from '../hooks/events';

const SaveThemeDialog = ({ theme }) => {
  const intl = useIntl();
  const {
    description,
    enabled,
    name,
    updateErrors,
    updateStatus,
  } = selectors.useSaveThemeDialog();
  const hide = events.useHideSaveTheme();
  const updateProperties = events.useUpdateActiveThemeProperties();
  const putData = events.useUpdateTheme();

  const validateForm = () => {
    const errors = [];

    checkRequired(name, [fields.NAME], errors, 'theme', 'save.theme.validation.name.title');
    checkMaxLength(name, [fields.NAME], errors, 'theme', 'save.theme.validation.name.title', globalFieldLengths.name);
    checkMaxLength(
      description,
      [fields.DESCRIPTION],
      errors,
      'theme',
      'save.theme.validation.description.title',
      globalFieldLengths.description,
    );

    return errors;
  };

  const submitForm = () => {
    const errors = validateForm();
    updateProperties({ properties: { updateErrors: errors } });

    if (errors.length === 0) {
      putData();
    }
  };

  const nameError = updateErrors.find((e) => (e.fields.includes(fields.NAME)));
  const descriptionError = updateErrors.find((e) => (e.fields.includes(fields.DESCRIPTION)));

  return (
    <DialogWrapper
      className="aridhia-save-theme-dialog"
      error={firstResponseError(updateErrors)}
      icon="paint brush"
      onClose={hide}
      onDismiss={() => updateProperties({ properties: { updateErrors: pruneServerErrors(updateErrors) } })}
      theme={theme}
      title={intl.formatMessage({ id: 'save.theme.create.title' })}
    >
      <Modal.Content scrolling>
        <Form autoComplete="off" onSubmit={submitForm} error={firstError(updateErrors) !== null}>
          <FormValidationError
            error={nameError}
            htmlFor="theme_name"
            labelId="save.theme.name"
          >
            <Input
              id="theme_name"
              autoFocus
              disabled={updateStatus === states.PENDING}
              type="custom"
              size="large"
              placeholder={intl.formatMessage({ id: 'save.theme.name.placeholder' })}
              value={name}
              maxLength={globalFieldLengths.name}
              onChange={(e) => updateProperties({ properties: { name: e.target.value } })}
              className={validationClasses(nameError)}
            />
          </FormValidationError>
          <FormValidationError
            error={descriptionError}
            htmlFor="theme_description"
            labelId="save.theme.description"
          >
            <MarkdownEditor
              id="theme_description"
              disabled={updateStatus === states.PENDING}
              value={description}
              onChange={(value) => updateProperties({ properties: { description: value } })}
              maxEditorHeight={80}
              minPreviewHeight={94}
              className={validationClasses(descriptionError)}
            />
          </FormValidationError>
        </Form>
      </Modal.Content>
      <Actions
        onClose={hide}
        inProgress={updateStatus === states.PENDING}
        onSave={submitForm}
        minimumDataEntryMet={name}
        saveMessageId="actions.modify"
      >
        <div className="aridhia-modal-actions-checkbox">
          <Checkbox
            label={intl.formatMessage({ id: 'actions.enabled' })}
            type="checkbox"
            disabled={updateStatus === states.PENDING}
            checked={enabled}
            onClick={() => updateProperties({ properties: { enabled: !enabled } })}
          />
        </div>
      </Actions>
    </DialogWrapper>
  );
};

export default SaveThemeDialog;
